import {Controller} from "@hotwired/stimulus"
import {get} from "@rails/request.js"

export default class extends Controller {
  static targets = [
    "eventSelect",
    "subeventSelect",
    "subjectField",
    "bodyField",
    "subeventContainer",
    "categoryWrapper",
    "allContacts",
    "groupCheckbox",
    "cohortCheckbox",
    "summaryTotal",
    "summaryEligibleInline",
    "summarySubscribed",
    "summaryNever",
    "summaryUnsubscribed",
    "summaryWillReceive",
    "testEmailError"
  ]

  static values = {
    recipientSummaryUrl: String,
    eventPromotionSubject: String,
    eventPromotionBody: String,
    ticketSalesSubject: String,
    ticketSalesBody: String,
    eventUpdatesSubject: String,
    eventUpdatesBody: String,
    customerSupportSubject: String,
    customerSupportBody: String,
    editView: { type: Boolean, default: false },
    // Event communication (Communications hub): the recipient summary must
    // bypass the marketing filter to match the actual transactional send.
    transactional: { type: Boolean, default: false },
    alertMessages: Object,
  }

  connect() {
    this.emailTemplates = {
      event_promotion: {
        subject: this.eventPromotionSubjectValue,
        body: this.eventPromotionBodyValue
      },
      ticket_sales: {
        subject: this.ticketSalesSubjectValue,
        body: this.ticketSalesBodyValue
      },
      event_updates: {
        subject: this.eventUpdatesSubjectValue,
        body: this.eventUpdatesBodyValue
      },
      customer_support: {
        subject: this.customerSupportSubjectValue,
        body: this.customerSupportBodyValue
      }
    }

    // (No longer prefilling a default template's subject/body on load — the
    // subject/preview come from the Details step and the body is AI-generated.)

    // Populate the Recipient Summary for the initial audience selection.
    if (this.hasSummaryWillReceiveTarget) this.refreshRecipientSummary()
  }

  // Recompute the Recipient Summary whenever the audience selection changes.
  // Goes through the same resolver SendCampaignJob uses, so "Will Send To"
  // equals what actually sends (subscribed minus global unsubscribes).
  async refreshRecipientSummary() {
    if (!this.hasRecipientSummaryUrlValue) return

    const params = new URLSearchParams()
    if (this.hasAllContactsTarget && this.allContactsTarget.checked) {
      params.set("include_all_contacts", "true")
    }
    if (this.hasGroupCheckboxTarget) {
      this.groupCheckboxTargets.filter(c => c.checked).forEach(c => params.append("group_ids[]", c.value))
    }
    if (this.hasCohortCheckboxTarget) {
      this.cohortCheckboxTargets.filter(c => c.checked).forEach(c => params.append("cohorts[]", c.value))
    }
    // Scope the event_attendees cohort to the selected event so the previewed
    // count matches what an event-scoped "Email attendees" send actually reaches.
    if (this.hasEventSelectTarget && this.eventSelectTarget.value) {
      params.set("event_id", this.eventSelectTarget.value)
    }
    // Event comms bypass the marketing filter — reflect that in the preview.
    if (this.transactionalValue) params.set("transactional", "1")

    const response = await get(`${this.recipientSummaryUrlValue}?${params.toString()}`, { responseKind: "json" })
    if (!response.ok) return
    const s = await response.json

    const set = (target, value) => { if (target) target.textContent = value }
    if (this.hasSummaryTotalTarget) set(this.summaryTotalTarget, s.total)
    if (this.hasSummaryEligibleInlineTarget) set(this.summaryEligibleInlineTarget, s.will_receive)
    if (this.hasSummarySubscribedTarget) set(this.summarySubscribedTarget, s.subscribed)
    if (this.hasSummaryNeverTarget) set(this.summaryNeverTarget, s.never_subscribed)
    if (this.hasSummaryUnsubscribedTarget) set(this.summaryUnsubscribedTarget, s.unsubscribed)
    if (this.hasSummaryWillReceiveTarget) set(this.summaryWillReceiveTarget, s.will_receive)
  }

  async eventChanged(event) {
    const eventId = event.target.value

    if (eventId) {
      try {
        const response = await get(`/organizer/campaigns/load_subevents?event_id=${eventId}`, {
          responseKind: "turbo-stream"
        })
      } catch (error) {
        console.error('Error loading subevents:', error)
      }
    } else {
      // Hide subevent container
      if (this.hasSubeventContainerTarget) {
        this.subeventContainerTarget.classList.add('d-none')
        this.subeventContainerTarget.innerHTML = ''
      }
    }

    // The event_attendees cohort is scoped to the selected event, so its
    // recipient count changes with the event — refresh the summary.
    this.refreshRecipientSummary()
  }

  async subeventChanged(event) {
    const subeventId = event.target.value
    if (subeventId){
      try {
        const response = await get(`/organizer/campaigns/change_subevent?subevent_id=${subeventId}`, {
          responseKind: "turbo-stream"
        })
      } catch (error) {
        console.error('Error loading subevent contacts:', error)
      }
    }
  }

  categoryChanged(event) {
    // Selection only — a Smart Template now sets the campaign's angle/category
    // and feeds "Generate Campaign"; it must NOT overwrite the subject the user
    // already entered on Details or dump canned body text.
    this.categoryWrapperTargets.forEach(wrapper => {
      wrapper.classList.remove('selected')
    })
    event.currentTarget.closest(`[data-contact-email-form-target="categoryWrapper"]`).classList.add('selected')
  }

  // Extract common form fields (subject, body, eventId, subeventId)
  extractFormFields() {
    const subject = this.subjectFieldTarget.value.trim()
    let body = ''
    let eventId = ''
    let subeventId = ''

    if (this.hasBodyFieldTarget) {
      body = (this.bodyFieldTarget.editor) ? this.bodyFieldTarget.innerHTML.trim() : this.bodyFieldTarget.value.trim()
    }

    if (this.hasEventSelectTarget) eventId = this.eventSelectTarget.value
    if (this.hasSubeventSelectTarget) subeventId = this.subeventSelectTarget.value

    return { subject, body, eventId, subeventId }
  }

  // Validate common required fields (subject, body, eventId)
  validateBasicFields(subject, body, eventId) {
    if (!subject || !body) {
      displayFlashMessage("error", this.alertMessagesValue["subjectAndBodyAlert"]);
      return false
    } else if (!eventId) {
      displayFlashMessage("error", this.alertMessagesValue["eventAlert"]);
      return false
    }
    return true
  }

  // Show a persistent inline message next to the Send Test Email button when a
  // prerequisite is unmet (e.g. no event selected) — the transient flash alone
  // was easy to miss, so the action read as silently failing.
  showTestEmailError(message) {
    this.testEmailErrorTargets.forEach((el) => {
      el.textContent = message
      el.hidden = false
    })
  }

  clearTestEmailError() {
    this.testEmailErrorTargets.forEach((el) => {
      el.textContent = ""
      el.hidden = true
    })
  }

  async sendTestEmail() {
    const { subject, body, eventId, subeventId } = this.extractFormFields()

    // Inline prerequisite messages (clear them on a valid attempt).
    if (!subject || !body) {
      this.showTestEmailError(this.alertMessagesValue["subjectAndBodyAlert"])
      return
    }
    if (!eventId) {
      this.showTestEmailError(this.alertMessagesValue["eventAlert"])
      return
    }
    this.clearTestEmailError()

    // Carry the "Include event card" toggle into the test send so the preview
    // matches what recipients get. Target the CHECKBOX specifically — Rails'
    // check_box renders a hidden "0" input with the same name first, and
    // .checked on that hidden input is always false (which dropped the card).
    const includeEventCard =
      document.querySelector('input[type="checkbox"][name="campaign[include_event_card]"]')?.checked ? "1" : "0"

    try {
      const response = await get(`/organizer/campaigns/send_test_email_modal?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}&event_id=${eventId}&subevent_id=${subeventId}&include_event_card=${includeEventCard}`, {
        responseKind: "turbo-stream"
      })
    } catch (error) {
      console.error('Error opening test email modal:', error)
      alert('Failed to open test email modal. Please try again.')
    }
  }

  async scheduleForLater() {
    // First validate the form using existing validation logic
    if (!this.validateFormData()) {
      return
    }

    // Collect form data
    const formData = this.collectFormData()

    try {
      const response = await get(`/organizer/campaigns/schedule_campaign_modal?${formData}`, {
        responseKind: "turbo-stream"
      })
    } catch (error) {
      console.error('Error opening schedule modal:', error)
      alert('Failed to open schedule modal. Please try again.')
    }
  }

  validateFormData() {
    const { subject, body, eventId } = this.extractFormFields()

    // Validate basic fields (subject, body, eventId)
    if (!this.validateBasicFields(subject, body, eventId)) {
      return false
    }

    // Check if at least one recipient is selected. Audience can be All Contacts,
    // a saved group, OR a built-in cohort (Event Attendees / Followers) — the
    // event "Send Message" flow uses the event_attendees cohort, so cohorts MUST
    // count here or a valid event-scoped send is wrongly blocked.
    const allContactsChecked = document.querySelector('input[name="campaign[include_all_contacts]"]')?.checked
    const groupsChecked = document.querySelectorAll('input[name="campaign[contact_group_ids][]"]:checked').length > 0
    const cohortsChecked = document.querySelectorAll('input[name="campaign[cohorts][]"]:checked').length > 0

    if (!allContactsChecked && !groupsChecked && !cohortsChecked) {
      displayFlashMessage("error", this.alertMessagesValue["RecipientGroupSelectAlert"])
      return false
    }

    return true
  }

  collectFormData() {
    const formData = new URLSearchParams()
    const { subject, body, eventId, subeventId } = this.extractFormFields()
    
    // Campaign data
    formData.append('campaign[subject]', subject)
    formData.append('campaign[body]', body)
    
    // Category (selected radio button)
    const selectedCategory = document.querySelector('input[name="campaign[category]"]:checked')
    if (selectedCategory) {
      formData.append('campaign[category]', selectedCategory.value)
    }

    // Event ID
    if (eventId) {
      formData.append('campaign[event_id]', eventId)
    }

    // Recipients
    const allContactsChecked = document.querySelector('input[name="campaign[include_all_contacts]"]')?.checked
    if (allContactsChecked) {
      formData.append('campaign[include_all_contacts]', 'true')
    } else {
      // Contact group IDs
      const checkedGroups = document.querySelectorAll('input[name="campaign[contact_group_ids][]"]:checked')
      checkedGroups.forEach(checkbox => {
        formData.append('campaign[contact_group_ids][]', checkbox.value)
      })
      // Built-in cohorts (Event Attendees / Followers) — without these the
      // event "Send Message" audience would be dropped on submit.
      const checkedCohorts = document.querySelectorAll('input[name="campaign[cohorts][]"]:checked')
      checkedCohorts.forEach(checkbox => {
        formData.append('campaign[cohorts][]', checkbox.value)
      })
    }

    // Event communication flag (bypasses marketing opt-in on send).
    const transactional = document.querySelector('input[name="campaign[transactional]"]')?.value
    if (transactional) formData.append('campaign[transactional]', transactional)

    // Subevent ID
    if (subeventId) {
      formData.append('subevent_id', subeventId)
    }

    return formData.toString()
  }

  prepareFormSubmission(event) {
    if (!this.validateFormData()) {
      event.preventDefault()
      return false
    }

    // Allow form submission to proceed
    return true
  }

  // Save as Draft: persist work-in-progress without sending. Unlike a real send,
  // a draft is allowed to be incomplete, so we skip recipient/field validation
  // and let the native submit proceed (the button posts commit=draft, which the
  // controller branches on). The server still requires a subject.
  saveAsDraft(event) {
    return true
  }
}